import type {
  ProposalDeclarationFieldSituacao,
  ProposalDeclarationResponse,
  ProposalDeclarationTextoOrigem,
  ProposalDocumentResponse,
  ProposalGuaranteeResponse,
  ProposalDocumentTipo,
  ProposalDocumentsResponse,
  ProposalRequirementCategoria,
  ProposalRequirementFileResponse,
  ProposalRequirementKindResponse,
  ProposalRequirementMomento,
  ProposalRequirementStatus,
} from 'services/actions/ai_management_proposal_documents';
import { DECLARATION_FIELDS, DeclarationFieldId } from 'services/declaration_templates';

export type ProposalRequirementMomentoKey = ProposalRequirementMomento;
export type ProposalRequirementKind = ProposalRequirementKindResponse;

export interface ProposalRequirementFile {
  key: string;
  name: string;
  sentBy: string | null;
  sentAt: string | null;
  url: string | null;
}

export interface DeclarationField {
  id: string;
  label: string;
  value: string | null;
  suggested: string | null;
  situacao: ProposalDeclarationFieldSituacao;
}

// Só nas declarações; nos documentos fica com os valores neutros.
export interface DeclarationBody {
  html: string;
  origem: ProposalDeclarationTextoOrigem | null;
  fields: DeclarationField[];
  needsProof: boolean;
  proofLabel: string | null;
  portal: boolean;
  modelId: string | null;
  suggestedModel: string | null;
}

// Declarações e documentos numa lista só: a etapa agrupa por momento, não por tipo. `tipo` da
// declaração é sempre 'declaracao'; o dos documentos vem do back (arquivo, amostra, garantia).
export interface ProposalRequirement {
  kind: ProposalRequirementKind;
  id: string;
  name: string;
  tipo: 'declaracao' | ProposalDocumentTipo;
  momento: ProposalRequirementMomento;
  categoria: ProposalRequirementCategoria;
  status: ProposalRequirementStatus;
  manual: boolean;
  excerpt: string | null;
  files: ProposalRequirementFile[];
  ignoredBy: string | null;
  ignoredAt: string | null;
  declaration: DeclarationBody | null;
  // Só na Garantia (tipo garantia): o estado que o back lê do ConLicitaSeg a cada consulta.
  guarantee: ProposalGuaranteeResponse | null;
}

export interface ProposalDocumentsState {
  requirements: ProposalRequirement[];
  progress: { confirmed: number; total: number };
  complete: boolean;
}

export const PROPOSAL_REQUIREMENT_MOMENTOS: ProposalRequirementMomento[] = [
  'antes_do_lance',
  'apos_lance',
];

export const MOMENTO_LABEL: Record<ProposalRequirementMomento, string> = {
  antes_do_lance: 'Antes do lance',
  apos_lance: 'Após o lance',
};

export const REQUIREMENT_STATUS: Record<
  ProposalRequirementStatus,
  { label: string; badge: string }
> = {
  pendente: { label: 'Pendente', badge: 'danger' },
  aguardando_revisao: { label: 'Aguardando revisão', badge: 'warning' },
  confirmado: { label: 'Confirmado', badge: 'success' },
  ignorada: { label: 'Exigência ignorada', badge: 'secondary' },
};

const toFile = (file: ProposalRequirementFileResponse): ProposalRequirementFile => ({
  key: file.key,
  name: file.nome,
  sentBy: file.enviado_por_nome ?? null,
  sentAt: file.enviado_em ?? null,
  url: file.url ?? null,
});

const toDeclarationBody = (item: ProposalDeclarationResponse): DeclarationBody => ({
  html: item.texto_final ?? '',
  origem: item.texto_origem ?? null,
  fields: (item.campos ?? []).map((campo) => ({
    id: campo.id,
    // Campo de modelo volta sem rótulo do back: o rótulo vem da fonte única dos modelos.
    label: campo.rotulo || DECLARATION_FIELDS[campo.id as DeclarationFieldId]?.rotulo || 'dado',
    value: campo.valor ?? null,
    suggested: campo.valor_sugerido ?? null,
    situacao: campo.situacao,
  })),
  needsProof: Boolean(item.pede_comprovacao),
  proofLabel: item.comprovacao || null,
  portal: Boolean(item.assinalar_no_portal),
  modelId: item.modelo_id ?? null,
  suggestedModel: item.modelo_sugerido ?? null,
});

const toRequirement = (
  kind: ProposalRequirementKind,
  item: ProposalDeclarationResponse | ProposalDocumentResponse,
): ProposalRequirement => ({
  kind,
  id: item.id,
  name: item.nome,
  tipo: kind === 'declaracoes' ? 'declaracao' : (item as ProposalDocumentResponse).tipo,
  momento: item.momento,
  categoria: item.categoria,
  status: item.status,
  manual: item.origem === 'manual',
  excerpt: item.trecho || null,
  files: (item.arquivos ?? []).map(toFile),
  ignoredBy: item.ignorada_por_nome ?? null,
  ignoredAt: item.ignorada_em ?? null,
  declaration:
    kind === 'declaracoes' ? toDeclarationBody(item as ProposalDeclarationResponse) : null,
  guarantee: kind === 'documentos' ? (item as ProposalDocumentResponse).garantia ?? null : null,
});

// Campo que ainda impede "Confirmar declaração": sugerido ou pendente, ou recusado sem valor.
export const fieldNeedsAction = (field: Pick<DeclarationField, 'situacao' | 'value'>) =>
  field.situacao === 'sugerido' ||
  field.situacao === 'pendente' ||
  (field.situacao === 'recusado' && !(field.value ?? '').trim());

export const toProposalDocumentsState = (
  body: ProposalDocumentsResponse,
): ProposalDocumentsState => ({
  requirements: [
    ...(body.declaracoes ?? []).map((item) => toRequirement('declaracoes', item)),
    ...(body.documentos ?? []).map((item) => toRequirement('documentos', item)),
  ],
  progress: {
    confirmed: body.progresso?.confirmadas ?? 0,
    total: body.progresso?.total ?? 0,
  },
  complete: Boolean(body.etapa_declaracoes_completa),
});

// Mesma regra do back (ProposalDocuments.pendencias_antes_do_lance), para o stepper acompanhar
// a tela: contam as "Antes do lance" nem confirmadas nem ignoradas.
export const pendenciasAntesDoLance = (requirements: ProposalRequirement[]) =>
  requirements.filter(
    (item) =>
      item.momento === 'antes_do_lance' &&
      item.status !== 'confirmado' &&
      item.status !== 'ignorada',
  ).length;
